/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import 'lazysizes';
import { debounce } from 'lodash';
import Rellax from 'rellax';
import Vue from 'vue';
import Headroom from 'headroom.js';
import { gsap } from 'gsap';
import Router from './router';

import {
    initSplitlinesReveal,
    initReveal,
    initImageReveal,
} from './modules/reveals';

require('./bootstrap');

require('./components/hero-section-component');
require('./components/product-slider');
require('./components/project-slider');
require('./components/project-listing');
require('./components/video-component');
require('./components/team-listing');
require('./components/contact-map');
require('./components/form-component');
require('./components/home-intro');
require('./components/image-slider');
require('./components/word-slider-component');

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            router: {
                currentSection: null,
                instance: null,
                prefetch: true,
            },
            isMobile: false,
            isTablet: false,
            showNavigation: false,
            inTransition: false,
            headroom: null,
        };
    },

    watch: {
        isMobile: function () {
            EventBus.$emit('is-mobile-changed');
        },
        isTablet: function () {
            EventBus.$emit('is-tablet-changed');
        },
        showNavigation: function (visibile) {
            EventBus.$emit('navigation-state-changed', visibile);
        },
    },

    mounted() {
        window.addEventListener('resize', this.onWindowResize);
        this.detectMobile();
        this.initHeadroom();

        Vue.nextTick(() => {
            initSplitlinesReveal();
            initReveal();
            initImageReveal();
            this.initRellax();
        });
    },

    methods: {
        /**
         * Raised when the client window is resizing.
         *
         * @return {Void}
         */
        onWindowResize: debounce(function () {
            EventBus.$emit('window-resize');
            this.detectMobile();
        }, 150),

        detectMobile() {
            this.isMobile = window.innerWidth <= 768;
            this.isTablet = window.innerWidth <= 1024;
        },

        toggleMenu() {
            if (!this.showNavigation) {
                if (!this.openMenuTl) {
                    this.openMenuTl = gsap.timeline({
                        paused: true,
                    });

                    this.openMenuTl.to('.mobile-navigation__background', {
                        duration: 1,
                        y: '0%',
                        ease: 'power4.out',
                    });

                    this.openMenuTl.to(
                        [
                            '.mobile-navigation__link-inner-line',
                            '.mobile-navigation__shop-icon',
                        ],
                        {
                            duration: 0.8,
                            y: '0%',
                            stagger: 0.1,
                            ease: 'power4.out',
                        },
                        0.2
                    );

                    this.openMenuTl.to(
                        '.mobile-navigation__list-border',
                        {
                            duration: 1,
                            scaleX: 1,
                            ease: 'power4.out',
                        },
                        '>-0.6'
                    );

                    this.openMenuTl.to(
                        [
                            '.mobile-navigation__info-title',
                            '.mobile-navigation__item--small',
                        ],
                        {
                            duration: 1,
                            autoAlpha: 1,
                            y: '0%',
                            stagger: 0.1,
                            ease: 'power4.out',
                        },
                        '<+0.2'
                    );
                }

                this.openMenuTl.play(0);

                this.showNavigation = !this.showNavigation;
            } else {
                const onComplete = () => {
                    this.showNavigation = !this.showNavigation;

                    gsap.set('.main-menu__mobile-toggle--close', {
                        clearProps: 'all',
                    });
                };

                if (!this.closeMenuTl) {
                    this.closeMenuTl = gsap.timeline({
                        paused: true,
                        onComplete: onComplete,
                    });

                    this.closeMenuTl.to(
                        [
                            '.mobile-navigation__link-inner-line',
                            '.mobile-navigation__shop-icon',
                        ],
                        {
                            duration: 0.4,
                            y: '100%',
                            stagger: -0.1,
                            ease: 'power4.out',
                        },
                        0
                    );

                    this.closeMenuTl.to(
                        '.mobile-navigation__list-border',
                        {
                            duration: 0.4,
                            scaleX: 0,
                            ease: 'power4.out',
                        },
                        0
                    );

                    this.closeMenuTl.to(
                        [
                            '.mobile-navigation__info-title',
                            '.mobile-navigation__item--small',
                        ],
                        {
                            duration: 0.4,
                            autoAlpha: 0,
                            stagger: -0.1,
                            ease: 'power4.out',
                        },
                        0
                    );

                    this.closeMenuTl.to(
                        '.mobile-navigation__background',
                        {
                            duration: 1,
                            y: '-100%',
                            ease: 'power4.out',
                        },
                        0.4
                    );

                    this.closeMenuTl.to(
                        '.main-menu__mobile-toggle--close',
                        {
                            duration: 0.2,
                            autoAlpha: 0,
                            ease: 'power4.out',
                        },
                        '>-0.6'
                    );
                }

                this.closeMenuTl.play(0);
            }
        },

        initHeadroom() {
            const header = document.querySelector('.header');
            this.headroom = new Headroom(header);
            this.headroom.init();
        },

        initRellax() {
            const viewportWidth =
                window.innerWidth || document.documentElement.clientWidth;
            if (viewportWidth >= 768) {
                this.rellax = new Rellax('.rellax');
            }
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    },
});
